'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Hl, Lesson, M, MB, NumberQuiz, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/probability'

const SoftmaxBars = dynamic(() => import('../_components/widgets/ProbWidgets').then(m => m.SoftmaxBars), { ssr: false })

export default function Softmax() {
  return (
    <Lesson>
      <Step>
        <H>From scores to probabilities</H>
        <P>
          A classifier looking at a photo produces one <Hl c="blue">score</Hl> per possible answer — cat 2.0, dog 1.0, bird
          0.1, fish −1.0. These raw scores (called <strong>logits</strong>) can be any number: negative, huge, whatever. But we
          want <Hl c="violet">probabilities</Hl>: every one positive, all adding up to exactly 1.
        </P>
        <P>
          <strong>Softmax</strong> does it in two moves. First, make everything positive by raising <M>e</M> to the power of
          each score (<M>{'e^x'}</M> is always positive, and bigger scores stay bigger). Then divide each by the total, so they
          add to 1:
        </P>
        <MB>{'\\violet{p_i} = \\frac{e^{\\blue{z_i}}}{\\sum_{j} e^{\\blue{z_j}}}'}</MB>
        <Figure n="5.8" caption="Drag the scores. Softmax keeps their order but turns them into a proper probability distribution.">
          <SoftmaxBars showTemperature={false} />
        </Figure>
      </Step>

      <Step gate={['nq-soft', 'q-shift']}>
        <H>Two properties worth knowing</H>
        <P>
          <strong>Only differences matter.</strong> Adding the same number to every score changes nothing, because it multiplies
          the top and bottom of the fraction by the same amount. That’s also the trick for computing it safely: subtract the
          biggest score first, so you never calculate <M>{'e^{1000}'}</M> (which is too big for a computer).
        </P>
        <NumberQuiz id="nq-soft" question={<>What probability does softmax give the first entry of <M>{'[0, 0]'}</M>?</>} answer={0.5} explain={<>e⁰ = 1 for both, so each gets 1 / (1 + 1) = 0.5. Equal scores, equal odds.</>} />
        <Quiz
          id="q-shift"
          question={<>Softmax of <M>{'[2, 1]'}</M> is <M>{'[0.73, 0.27]'}</M>. What is softmax of <M>{'[102, 101]'}</M>?</>}
          options={[{ label: <M>{'[0.73, 0.27]'}</M>, correct: true }, { label: <M>{'[1.0, 0.0]'}</M>, why: 'The scores are bigger, but the difference between them is still 1.' }, { label: <M>{'[0.5, 0.5]'}</M>, why: 'They’re not equal — one is still 1 more than the other.' }]}
          explain="Only the gaps between scores matter, and the gap is still 1."
        />
      </Step>

      <Step gate="softmax-impl">
        <H>Implement softmax</H>
        <Exercise
          id="softmax-impl"
          title="A numerically safe softmax"
          {...EX.softmaxImpl}
          hints={[
            <>
              <C>const m = Math.max(...scores)</C> finds the biggest score. Then <C>{'scores.map(s => Math.exp(s - m))'}</C>.
            </>,
            <>Add up the exponentials, then divide each by the total.</>,
          ]}
        />
      </Step>

      <Step gate="q-temp">
        <H>Temperature: dialling confidence</H>
        <P>
          Divide every score by a number <M>T</M> before the softmax, and you control how “sharp” the distribution is. This{' '}
          <strong>temperature</strong> is the same knob you may have seen on AI chat tools:
        </P>
        <MB>{'\\violet{p_i} = \\frac{e^{\\blue{z_i}/T}}{\\sum_j e^{\\blue{z_j}/T}}'}</MB>
        <Figure n="5.9" caption="Turn the temperature down and the top choice takes almost everything; turn it up and all choices become nearly equal.">
          <SoftmaxBars />
        </Figure>
        <Exercise id="temperature" title="Temperature in code" {...EX.temperature} />
        <Quiz
          id="q-temp"
          question="A chatbot gives the same safe answer every time. Which temperature change would make it more varied and creative?"
          options={[{ label: 'Raise the temperature', correct: true }, { label: 'Lower the temperature', why: 'Lower temperature makes the top choice even more dominant — less variety.' }, { label: 'Temperature doesn’t affect variety', why: 'It directly reshapes the probabilities the next word is chosen from.' }]}
          explain="Higher T flattens the distribution, so less-likely words get picked more often. You’ll play with this on a real model in Module 8."
        />
        <Callout kind="why">
          Softmax sits at the end of nearly every classifier and every language model. When GPT “predicts the next word”, it’s
          computing a softmax over a vocabulary of ~100,000 tokens.
        </Callout>
      </Step>
    </Lesson>
  )
}
